import styled from '@emotion/styled';

import { screen } from '@/components/common/breakpoints';
import { BulletPoints } from '@/components/common/bulletpoints';
import { CaptionHeadingDescription } from '@/components/common/CaptionHeadingDescription';
import { RichHeading } from '@/components/common/Heading';
import { Image } from '@/components/common/image';
import { HeadingLevel } from '@/components/common/MarkUp';
import { PortableText } from '@/components/common/utils';
import { Section } from '@/components/layout/Section';
import { getBulletPointColour } from '@/sanity/schemas/objects/colours/colourUtils';

import type { BlockWithAudience } from '@/types/shared';

import type { ImageBulletPointsDescription as SanityImageBulletPointsDescription } from '@/sanity/sanity.types'

export interface ImageBulletPointsDescriptionProps
  extends SanityImageBulletPointsDescription,
  BlockWithAudience { }

type ColumnGridType = {
  multiColumn: boolean;
};

export const ImageBulletPointsDescription = ({
  heading,
  subheading,
  image,
  text,
  bulletPoints,
  multiColumnBullets,
  backgroundColour,
  blockSpacing,
  paddingAdjustment,
  pageAudience,
  isAudienceSwitcherEnabled,
}: ImageBulletPointsDescriptionProps) => {
  const backgroundColourToken = backgroundColour?.token;
  const bulletPointColourToken = getBulletPointColour(backgroundColourToken);
  return (
    <Section
      verticalPadding={paddingAdjustment ?? true}
      spacing={blockSpacing}
      backgroundColour={backgroundColourToken}
      className="image-bullet-points-description"
    >
      <OuterWrapper>
        <TextWrapper>
          <HeadingLevel>
            <StyledCaptionHeadingDescription
              heading={heading}
              description={text}
              padding={{ description: 'x-large', descriptionMobile: 'x-large' }}
            />
            {subheading && (
              <HeadingLevel>
                <RichHeading heading={subheading} size="label-lg" />
              </HeadingLevel>
            )}
          </HeadingLevel>
          <BulletPoints
            pointColour={bulletPointColourToken}
            bulletPointStyle="hexagon"
          >
            <ColumnGrid multiColumn={multiColumnBullets}>
              <PortableText
                value={bulletPoints}
                audienceConfig={{ pageAudience, isAudienceSwitcherEnabled }}
              />
            </ColumnGrid>
          </BulletPoints>
        </TextWrapper>
        <ImageWrapper>
          <Image {...image} radius="l" />
        </ImageWrapper>
      </OuterWrapper>
    </Section>
  );
};

const OuterWrapper = styled.div`
  display: flex;
  flex-direction: column;

  ${screen.md} {
    flex-direction: row;
  }
`;

const TextWrapper = styled.div`
  flex: 0 0 50%;
  padding: calc(var(--spacing-800) / 2);
  ${screen.md} {
    padding: 0 var(--spacing-500) 0 var(--spacing-1600);
  }
`;

const ImageWrapper = styled.div`
  width: 100%;
`;

const StyledCaptionHeadingDescription = styled(CaptionHeadingDescription)`
  p {
    padding-bottom: var(--spacing-500);
    ${screen.md} {
      padding-bottom: var(--spacing-400);
    }
  }
`;
const ColumnGrid = styled.div<ColumnGridType>`
  padding-top: var(--spacing-300);
  ${screen.sm} {
    ul {
      display: grid;
      grid-template-columns: ${(props) =>
    props.multiColumn ? 'repeat(2, minmax(200px, 1fr))' : '400px'};
    }
  }
`;
